import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-0 focus:border-0 focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Existing shadcn/ui variants (kept for backward compatibility)
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Runcards mobile design variants (matches mobile app)
        "runcards-primary":
          "bg-runcards-primary font-runcards-bold hover:opacity-90",
        "runcards-secondary":
          "bg-runcards-surface text-runcards-text-primary border border-runcards-border font-runcards-bold hover:bg-runcards-foreground",
        "runcards-outline":
          "bg-transparent text-runcards-text-primary border border-runcards-text-primary font-runcards-bold hover:bg-runcards-surface",
        // Liquid-glass variants (see .rc-glass / .rc-glass-dark in index.css):
        // glass = white frosted (dark/image bg); glass-dark = smoked (light bg).
        "runcards-glass": "rc-glass font-runcards-bold",
        "runcards-glass-dark": "rc-glass-dark font-runcards-bold",
      },
      size: {
        // Existing shadcn/ui sizes (kept for backward compatibility)
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        // Runcards mobile design sizes (matches mobile app)
        "runcards-small": "h-9 px-runcards-md py-runcards-sm rounded-runcards-md text-runcards-sm min-h-[36px]",
        "runcards-medium": "h-10 px-runcards-md py-runcards-sm rounded-runcards-md text-runcards-md min-h-[40px]",
        "runcards-large": "h-14 px-runcards-xl py-runcards-lg rounded-runcards-md text-runcards-lg min-h-[56px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, style, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  const isPrimary = variant === "runcards-primary"
  // Force black text color for primary buttons to override global white text
  // Using inline style which has higher specificity than CSS classes
  const buttonStyle = isPrimary 
    ? { ...style, color: '#000000' } 
    : style
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      style={buttonStyle}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
